import * as React from 'react';
import { CheckIcon, ChevronDown, XIcon } from 'lucide-react';

import { cn } from '../../../lib/utils';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '../../ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '../../ui/popover';
import { Separator } from '../../ui/separator';
import { DEFAULT_MULTI_SELECT_MAX_COUNT } from '../multi-select/multi-select';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface GroupedMultiSelectOption {
  /** Child label */
  label: string;
  /** Child value */
  value: string;
}

export interface GroupedMultiSelectGroup {
  /** Children options within this group */
  children: GroupedMultiSelectOption[];
  /** Group header label */
  label: string;
  /** Group value (used for group-level toggle logic) */
  value: string;
}

export interface GroupedMultiSelectProps {
  /** CSS className for the trigger button */
  className?: string;
  /** Default selected values */
  defaultValue?: string[];
  /** Text when no options match search */
  emptyText?: string;
  /** Grouped options */
  groups: GroupedMultiSelectGroup[];
  /** Maximum badges to show before +N overflow */
  maxCount?: number;
  /** Callback when selection changes */
  onValueChange: (values: string[]) => void;
  /** Placeholder text when nothing selected */
  placeholder?: string;
  /** Whether search input is shown */
  searchable?: boolean;
  /** Search input placeholder text */
  searchPlaceholder?: string;
  /** "Select all" text */
  selectAllText?: string;
  /** "Unselect all" text */
  unselectAllText?: string;
}

// ─── State hook ───────────────────────────────────────────────────────────────

interface UseGroupedMultiSelectReturn {
  handleClear: () => void;
  handleSelectAll: (allChildValues: string[]) => void;
  handleTogglePopover: () => void;
  isPopoverOpen: boolean;
  selectedValues: string[];
  setIsPopoverOpen: React.Dispatch<React.SetStateAction<boolean>>;
  toggleGroup: (group: GroupedMultiSelectGroup) => void;
  toggleOption: (value: string) => void;
}

function useGroupedMultiSelect(
  defaultValue: string[] | undefined,
  onValueChange: (values: string[]) => void,
): UseGroupedMultiSelectReturn {
  const [selectedValues, setSelectedValues] = React.useState<string[]>(defaultValue ?? []);
  const [isPopoverOpen, setIsPopoverOpen] = React.useState(false);

  function handleTogglePopover(): void {
    setIsPopoverOpen((prev) => !prev);
  }

  function handleClear(): void {
    setSelectedValues([]);
    onValueChange([]);
  }

  function handleSelectAll(allChildValues: string[]): void {
    setSelectedValues(allChildValues);
    onValueChange(allChildValues);
  }

  function toggleOption(value: string): void {
    const next = selectedValues.includes(value)
      ? selectedValues.filter((v) => v !== value)
      : [...selectedValues, value];
    setSelectedValues(next);
    onValueChange(next);
  }

  function toggleGroup(group: GroupedMultiSelectGroup): void {
    const childValues = group.children.map((c) => c.value);
    const allSelected = childValues.length > 0 && childValues.every((v) => selectedValues.includes(v));

    const next = allSelected
      ? selectedValues.filter((v) => !childValues.includes(v))
      : [...selectedValues.filter((v) => !childValues.includes(v)), ...childValues];

    setSelectedValues(next);
    onValueChange(next);
  }

  return {
    handleClear,
    handleSelectAll,
    handleTogglePopover,
    isPopoverOpen,
    selectedValues,
    setIsPopoverOpen,
    toggleGroup,
    toggleOption,
  };
}

// ─── Sub-components ───────────────────────────────────────────────────────────

interface GroupedCheckboxItemProps {
  indent?: boolean;
  isSelected: boolean;
  label: string;
  onToggle: () => void;
}

function GroupedCheckboxItem({ indent, isSelected, label, onToggle }: GroupedCheckboxItemProps): React.JSX.Element {
  return (
    <CommandItem className={cn('cursor-pointer py-1.5', indent && 'ps-6')} onSelect={onToggle}>
      <div
        className={cn(
          'me-1.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border border-primary',
          isSelected ? 'bg-primary text-primary-foreground' : 'opacity-50 [&_svg]:invisible',
        )}
      >
        <CheckIcon className="h-4 w-4" />
      </div>
      <span>{label}</span>
    </CommandItem>
  );
}

interface GroupCommandGroupProps {
  group: GroupedMultiSelectGroup;
  selectedValues: string[];
  toggleGroup: (group: GroupedMultiSelectGroup) => void;
  toggleOption: (value: string) => void;
}

function GroupCommandGroup({
  group,
  selectedValues,
  toggleGroup,
  toggleOption,
}: GroupCommandGroupProps): React.JSX.Element {
  const childValues = group.children.map((c) => c.value);
  const allGroupSelected = childValues.length > 0 && childValues.every((v) => selectedValues.includes(v));

  return (
    <CommandGroup key={group.value}>
      <GroupedCheckboxItem isSelected={allGroupSelected} label={group.label} onToggle={() => toggleGroup(group)} />
      {group.children.map((child) => (
        <GroupedCheckboxItem
          indent
          isSelected={selectedValues.includes(child.value)}
          key={child.value}
          label={child.label}
          onToggle={() => toggleOption(child.value)}
        />
      ))}
    </CommandGroup>
  );
}

interface GroupedSelectTriggerProps {
  allOptions: GroupedMultiSelectOption[];
  handleClear: () => void;
  maxCount: number;
  placeholder: string;
  selectedValues: string[];
}

function GroupedSelectTrigger({
  allOptions,
  handleClear,
  maxCount,
  placeholder,
  selectedValues,
}: GroupedSelectTriggerProps): React.JSX.Element {
  if (selectedValues.length === 0) {
    return (
      <div className="mx-auto flex w-full items-center justify-between">
        <span className="text-sm">{placeholder}</span>
        <ChevronDown className="ms-2 h-4 w-4 cursor-pointer text-muted-foreground" />
      </div>
    );
  }

  const visibleSelected = selectedValues.slice(0, maxCount);
  const overflowCount = selectedValues.length - maxCount;

  return (
    <div className="flex w-full items-center justify-between">
      <div className="flex flex-wrap items-center">
        {visibleSelected.map((value) => {
          const option = allOptions.find((o) => o.value === value);
          if (!option) return null;
          return (
            <Badge
              className="mx-0.5 my-0.5 h-5 max-w-[7rem] shrink-0 gap-0.5 border-transparent bg-badge px-1.5 text-xs font-normal text-badge-foreground hover:bg-badge/80"
              key={value}
            >
              {option.label}
            </Badge>
          );
        })}
        {overflowCount > 0 && (
          <span className="ms-1.5 text-sm text-muted-foreground">{`+${String(overflowCount)}`}</span>
        )}
      </div>
      <div className="flex items-center justify-between">
        <span
          aria-label="Clear all"
          className="mx-2 inline-flex cursor-pointer items-center text-muted-foreground"
          onClick={(event) => {
            event.stopPropagation();
            handleClear();
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              event.stopPropagation();
              handleClear();
            }
          }}
          role="button"
          tabIndex={0}
        >
          <XIcon aria-hidden="true" className="h-4 w-4" />
        </span>
        <Separator className="flex h-full min-h-6" orientation="vertical" />
        <ChevronDown className="ms-2 h-4 w-4 cursor-pointer text-muted-foreground" />
      </div>
    </div>
  );
}

interface SelectAllGroupProps {
  allSelected: boolean;
  onToggle: () => void;
  selectAllText: string;
  unselectAllText: string;
}

function SelectAllGroup({
  allSelected,
  onToggle,
  selectAllText,
  unselectAllText,
}: SelectAllGroupProps): React.JSX.Element {
  return (
    <CommandGroup>
      <GroupedCheckboxItem
        isSelected={allSelected}
        label={allSelected ? unselectAllText : selectAllText}
        onToggle={onToggle}
      />
      <CommandSeparator />
    </CommandGroup>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

function deriveAllOptions(groups: GroupedMultiSelectGroup[]): GroupedMultiSelectOption[] {
  return groups.flatMap((g) => g.children);
}

function isAllSelected(allChildValues: string[], selectedValues: string[]): boolean {
  return allChildValues.length > 0 && allChildValues.every((v) => selectedValues.includes(v));
}

export function GroupedMultiSelect({
  className,
  defaultValue,
  emptyText = 'No results found.',
  groups,
  maxCount = DEFAULT_MULTI_SELECT_MAX_COUNT,
  onValueChange,
  placeholder = 'Select options',
  searchable = false,
  searchPlaceholder = 'Search...',
  selectAllText = 'Select All',
  unselectAllText = 'Unselect All',
}: GroupedMultiSelectProps): React.JSX.Element {
  const {
    handleClear,
    handleSelectAll,
    handleTogglePopover,
    isPopoverOpen,
    selectedValues,
    setIsPopoverOpen,
    toggleGroup,
    toggleOption,
  } = useGroupedMultiSelect(defaultValue, onValueChange);

  const allOptions = deriveAllOptions(groups);
  const allChildValues = allOptions.map((o) => o.value);
  const allSelected = isAllSelected(allChildValues, selectedValues);

  const closePopover = React.useCallback((): void => setIsPopoverOpen(false), [setIsPopoverOpen]);

  function handleToggleAll(): void {
    if (allSelected) {
      handleClear();
    } else {
      handleSelectAll(allChildValues);
    }
  }

  return (
    <Popover onOpenChange={setIsPopoverOpen} open={isPopoverOpen}>
      <PopoverTrigger asChild>
        <Button
          className={cn(
            'flex h-auto min-h-9 w-full items-center justify-between rounded-md border bg-inherit [&_svg]:pointer-events-auto',
            className,
          )}
          onClick={handleTogglePopover}
          variant="outline"
        >
          <GroupedSelectTrigger
            allOptions={allOptions}
            handleClear={handleClear}
            maxCount={maxCount}
            placeholder={placeholder}
            selectedValues={selectedValues}
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-0" onEscapeKeyDown={closePopover}>
        <Command className="[&_[cmdk-input-wrapper]]:mx-2 [&_[cmdk-input-wrapper]]:my-2 [&_[cmdk-input-wrapper]]:rounded-md [&_[cmdk-input-wrapper]]:border [&_[cmdk-input-wrapper]]:border-input [&_[cmdk-input-wrapper]]:border-b [&_[cmdk-input-wrapper]]:px-2 [&_[cmdk-input-wrapper]:focus-within]:ring-2 [&_[cmdk-input-wrapper]:focus-within]:ring-ring [&_[cmdk-input-wrapper]:focus-within]:ring-offset-2 [&_[cmdk-input]]:h-8 [&_[cmdk-input]]:py-1.5">
          {searchable && <CommandInput placeholder={searchPlaceholder} />}
          {searchable && <CommandSeparator />}
          <CommandList>
            <CommandEmpty>{emptyText}</CommandEmpty>
            <SelectAllGroup
              allSelected={allSelected}
              onToggle={handleToggleAll}
              selectAllText={selectAllText}
              unselectAllText={unselectAllText}
            />
            {groups.map((group) => (
              <GroupCommandGroup
                group={group}
                key={group.value}
                selectedValues={selectedValues}
                toggleGroup={toggleGroup}
                toggleOption={toggleOption}
              />
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
